import type { Route } from '@/services/router/routes';
import type { ProductTaxonomy } from '@/services/product-taxonomy';

/**
 * Human-readable labels for command palette navigation search.
 * Labels match what users see in breadcrumbs and the UI nav.
 * Routes not listed here fall back to auto-generated labels from path segments.
 * Update this file when adding or renaming pages.
 *
 * Static labels whose route key and text never change. Routes whose label and/or URL
 * segment are renamed per-account by the Journeys taxonomy live in `getNavigationLabels`
 * below, not here. Prefer `getNavigationLabels(taxonomy)` over reading this directly.
 */
const STATIC_NAVIGATION_LABELS = Object.freeze({
  // ── Home ──────────────────────────────────────────────────────────────────
  '/workspaces/:workspaceId/home': 'Home',
  '/workspaces/:workspaceId/home/add-people': 'Home / Add People',

  // ── Agent ─────────────────────────────────────────────────────────────────
  '/workspaces/:workspaceId/agent': 'Agent',
  '/workspaces/:workspaceId/agent/routines': 'Agent / Routines',
  '/workspaces/:workspaceId/agent/skills': 'Agent / Skills',

  // ── Health ────────────────────────────────────────────────────────────────
  '/workspaces/:workspaceId/health': 'Workspace Health',

  // ── Analysis ─────────────────────────────────────────────────────────────
  '/workspaces/:workspaceId/analysis/deliverability': 'Deliverability Analysis',

  // ── Design Studio ─────────────────────────────────────────────────────────
  '/workspaces/:workspaceId/design-studio/dashboard': 'Design Studio',
  '/workspaces/:workspaceId/design-studio/global-styles': 'Design Studio / Global Styles',

  // ── Send messages ─────────────────────────────────────────────────────────
  // Note: the Campaigns/Automations, Newsletters/One-time sends and API-triggered broadcasts/API-triggered workflow
  // entries are taxonomy-gated — see getNavigationLabels below.
  '/workspaces/:workspaceId/journeys/broadcasts': 'Broadcasts',
  '/workspaces/:workspaceId/journeys/transactional': 'Transactional',
  '/workspaces/:workspaceId/journeys/anonymous': 'Anonymous',
  '/workspaces/:workspaceId/journeys/message-library': 'Message Library',

  // ── Create content ────────────────────────────────────────────────────────
  '/workspaces/:workspaceId/assets': 'Assets',
  '/workspaces/:workspaceId/journeys/layouts': 'Email Layouts',
  '/workspaces/:workspaceId/journeys/layouts/starters': 'Email Layouts / Starters',
  '/workspaces/:workspaceId/snippets': 'Snippets',

  // ── Manage audience ───────────────────────────────────────────────────────
  // Note: the People/Profiles entry is taxonomy-gated — see getNavigationLabels below.
  '/workspaces/:workspaceId/journeys/segments': 'Segments',
  '/workspaces/:workspaceId/journeys/segments/explore': 'Segment Explorer',
  '/workspaces/:workspaceId/journeys/goals': 'Goals',
  '/workspaces/:workspaceId/journeys/objects': 'Custom Objects',

  // ── Review ────────────────────────────────────────────────────────────────
  // Note: the Analysis/Reports and Activity Logs/Profile activity entries are taxonomy-gated
  // — see getNavigationLabels below.
  '/workspaces/:workspaceId/journeys/outbox': 'Deliveries & Drafts',
  '/workspaces/:workspaceId/journeys/outbox/deliveries': 'Deliveries',
  '/workspaces/:workspaceId/journeys/outbox/drafts': 'Drafts',

  // ── Configure data ────────────────────────────────────────────────────────
  '/workspaces/:workspaceId/journeys/data-index': 'Data Index',
  '/workspaces/:workspaceId/journeys/integrations': 'Integrations',
  '/workspaces/:workspaceId/journeys/integrations/reporting-webhooks': 'Integrations / Reporting Webhooks',
  '/workspaces/:workspaceId/journeys/integrations/warehouse': 'Integrations / Warehouse',
  '/workspaces/:workspaceId/journeys/imports': 'Imports',
  '/workspaces/:workspaceId/journeys/exports': 'Exports',
  '/workspaces/:workspaceId/journeys/collections': 'Collections',

  // ── Pipelines (CDP) ───────────────────────────────────────────────────────
  '/workspaces/:workspaceId/pipelines': 'Pipelines',
  '/workspaces/:workspaceId/pipelines/sources': 'Pipelines / Sources',
  '/workspaces/:workspaceId/pipelines/sources/journeys': 'Pipelines / Sources / Journeys',
  '/workspaces/:workspaceId/pipelines/sources/reverse-etl': 'Pipelines / Sources / Reverse ETL',
  '/workspaces/:workspaceId/pipelines/destinations': 'Pipelines / Destinations',
  '/workspaces/:workspaceId/pipelines/destinations/journeys': 'Pipelines / Destinations / Journeys',
  '/workspaces/:workspaceId/pipelines/connections': 'Pipelines / Connections',
  '/workspaces/:workspaceId/pipelines/catalog': 'Pipelines / Catalog',

  // ── Workspace settings (Hydra-owned) ──────────────────────────────────────
  '/workspaces/:workspaceId/settings': 'Workspace settings',
  '/workspaces/:workspaceId/settings/llm_configuration': 'Workspace settings / LLM Configuration',
  '/workspaces/:workspaceId/settings/ai-business-profile': 'Workspace settings / Business context',
  '/workspaces/:workspaceId/settings/audit-logs': 'Workspace settings / Audit logs',
  '/workspaces/:workspaceId/settings/api-keys': 'Workspace settings / API keys',

  // ── Workspace settings (Ember-owned, routed via /settings/* catch-all) ───
  '/workspaces/:workspaceId/settings/edit': 'Workspace settings / General',
  '/workspaces/:workspaceId/settings/api_credentials': 'Workspace settings / API credentials',
  '/workspaces/:workspaceId/settings/language': 'Workspace settings / Language',
  '/workspaces/:workspaceId/settings/merge_options': 'Workspace settings / Merge options',
  '/workspaces/:workspaceId/settings/webhook_configuration': 'Workspace settings / Webhooks',
  '/workspaces/:workspaceId/settings/subscription_center': 'Workspace settings / Subscription Center',
  '/workspaces/:workspaceId/settings/subscription_center/branding/edit':
    'Workspace settings / Subscription Center / Branding',
  '/workspaces/:workspaceId/settings/subscription_center/settings':
    'Workspace settings / Subscription Center / Settings',
  '/workspaces/:workspaceId/settings/subscription_center/localization/edit':
    'Workspace settings / Subscription Center / Localization',
  '/workspaces/:workspaceId/settings/objects': 'Workspace settings / Custom Object types',
  '/workspaces/:workspaceId/settings/suppressions': 'Workspace settings / Suppressions',
  // ── Workspace settings / channel actions ──────────────────────────────────
  '/workspaces/:workspaceId/settings/actions/email': 'Workspace settings / Email',
  '/workspaces/:workspaceId/settings/actions/email/sending_domains': 'Workspace settings / Email / Sending Domains',
  '/workspaces/:workspaceId/settings/actions/email/smtp': 'Workspace settings / Email / SMTP',
  '/workspaces/:workspaceId/settings/actions/email/suppression_list': 'Workspace settings / Email / Suppression List',
  '/workspaces/:workspaceId/settings/actions/email/account_verification':
    'Workspace settings / Email / Account verification',
  '/workspaces/:workspaceId/settings/actions/email/defaults': 'Workspace settings / Email / Defaults',
  '/workspaces/:workspaceId/settings/actions/sms': 'Workspace settings / SMS',
  '/workspaces/:workspaceId/settings/actions/push': 'Workspace settings / Push',
  '/workspaces/:workspaceId/settings/actions/in_app': 'Workspace settings / In-App',
  '/workspaces/:workspaceId/settings/actions/slack': 'Workspace settings / Slack',
  '/workspaces/:workspaceId/settings/actions/twilio': 'Workspace settings / Twilio SMS',
  '/workspaces/:workspaceId/settings/actions/sinch': 'Workspace settings / Sinch SMS',
  '/workspaces/:workspaceId/settings/actions/whatsapp': 'Workspace settings / WhatsApp',
  '/workspaces/:workspaceId/settings/actions/line': 'Workspace settings / LINE',
  '/workspaces/:workspaceId/settings/actions/messaging_limit': 'Workspace settings / Messaging Limit',
  '/workspaces/:workspaceId/settings/actions/time_zone_match': 'Workspace settings / Time Zone Matching',
  '/workspaces/:workspaceId/settings/actions/url_params': 'Workspace settings / URL Parameters',

  // ── Account settings ──────────────────────────────────────────────────────
  '/settings': 'Account Settings',
  '/settings/audit-logs': 'Account Settings / Audit Logs',
  '/settings/account': 'Account Settings / Account',
  '/settings/team': 'Account Settings / Team',
  '/settings/billing': 'Account Settings / Billing',
  '/settings/billing/your-plan': 'Account Settings / Billing / Your Plan',
  '/settings/billing/available-plans': 'Account Settings / Billing / Available Plans',
  '/settings/billing/payment-methods': 'Account Settings / Billing / Payment Methods',
  '/settings/billing/billing-history': 'Account Settings / Billing / Billing History',
  '/settings/security': 'Account Settings / Security',
  '/settings/security/sso': 'Account Settings / Security / SSO',
  '/settings/personal': 'Personal Settings',
  '/settings/personal/security': 'Personal Settings / Security',
  '/settings/roles': 'Account Settings / Roles',
  '/settings/privacy': 'Account Settings / Privacy',
  '/settings/ai': 'Account Settings / AI & MCP',
  '/settings/workspaces': 'Account Settings / Workspaces',
  '/settings/subscription': 'Account Settings / Subscription',
  '/settings/receipts': 'Account Settings / Receipts',
  '/settings/api_credentials': 'Account Settings / API Credentials',
  '/settings/api-keys': 'Account Settings / API Keys',
  '/settings/experimental-features': 'Account Settings / Experimental Features',
} satisfies Partial<Record<Route, string>>);

/**
 * Full route→label map for command palette navigation, including routes whose label
 * and/or URL segment are renamed per-account by the Journeys taxonomy (resolved via the
 * passed taxonomy). This is the single source of truth for navigable route definitions —
 * add future renamed routes to the taxonomy-gated block below, keyed by `taxonomy.route(…)`.
 */
export function getNavigationLabels(taxonomy: ProductTaxonomy): Record<string, string> {
  return {
    ...STATIC_NAVIGATION_LABELS,

    // ── Taxonomy-gated (Journeys taxonomy rename) ─────────────────────────────
    // Both the route key and the label depend on the active taxonomy variant.
    // Review › Analysis → Reports
    [taxonomy.route('analysis.index')]: taxonomy.t('nav.review.analysis'),
    // Review › Activity Logs → Profile activity
    [taxonomy.route('profileActivity.index')]: taxonomy.t('nav.review.activityLogs'),
    // Manage audience › People → Profiles
    [taxonomy.route('people.index')]: taxonomy.t('nav.manageAudience.people'),
    // Send messages › Campaigns → Automations
    [taxonomy.route('campaigns.index')]: taxonomy.t('nav.sendMessages.campaigns'),
    // Send messages › Newsletters → One-time sends
    [taxonomy.route('broadcasts.oneTimeSends')]: `Broadcasts / ${taxonomy.t('nav.broadcasts.newsletters')}`,
    // Send messages › API-triggered broadcasts → API-triggered workflow
    [taxonomy.route('broadcasts.apiTriggered')]: `Broadcasts / ${taxonomy.t('nav.broadcasts.apiTriggered')}`,
  };
}

// Routes that can't be expressed as a typed Route key (e.g. URL + query params).
// Merged into the navigable routes alongside getNavigationLabels().
export const NAVIGATION_EXTRAS: Record<string, string> = {
  '/workspaces/:workspaceId/settings/api_credentials?keyType=tracking':
    'Workspace settings / API credentials / Tracking API keys',
  '/workspaces/:workspaceId/settings/api_credentials?keyType=app':
    'Workspace settings / API credentials / App API keys',
  '/workspaces/:workspaceId/settings/api_credentials?keyType=signing':
    'Workspace settings / API credentials / Webhook signing keys',
  '/workspaces/:workspaceId/settings/api_credentials?keyType=service_accounts':
    'Workspace settings / API credentials / Service accounts',
  '/workspaces/:workspaceId/health?tab=services': 'Workspace Health / Services',
  '/workspaces/:workspaceId/health?tab=data': 'Workspace Health / Data',
  '/workspaces/:workspaceId/health?tab=messaging': 'Workspace Health / Messaging',
};
